// Dashboard Shared Components
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Gift, HelpCircle, ChevronDown, Home, CreditCard, PenTool, Folder, Scan,
  Shield, Lock, LogOut, User, ChevronRight, X, Bell, Bot, Sparkles, Menu,
  Zap, Target, FileText, Package
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { PACKS } from '../../../constants/dashboard';

// Onboarding Tooltip Component
export const OnboardingTooltip = ({ step, targetRef, onNext, onSkip, isVisible }) => {
  const tooltips = [
    { title: "📂 Étape 1: Upload", content: "Commencez par uploader votre relevé bancaire dans l'onglet Documents", position: "bottom" },
    { title: "🔍 Étape 2: Scan IA", content: "Utilisez l'onglet Audit IA pour scanner vos documents et détecter les abonnements", position: "bottom" },
    { title: "✍️ Étape 3: Signature", content: "Signez votre Mandat de Succès pour nous autoriser à agir en votre nom", position: "bottom" },
    { title: "🎯 Étape 4: Suivi", content: "Suivez l'avancement de vos résiliations dans l'aperçu", position: "bottom" }
  ];
  
  if (!isVisible || step > tooltips.length) return null;
  const tooltip = tooltips[step - 1];
  
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
        className="fixed z-50 bg-cyan-400 text-black p-4 rounded-lg shadow-xl max-w-xs" style={{ top: '120px', left: '50%', transform: 'translateX(-50%)' }}>
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-cyan-400 rotate-45" />
        <h4 className="font-orbitron font-bold mb-2">{tooltip.title}</h4>
        <p className="text-sm mb-4">{tooltip.content}</p>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" size="sm" onClick={onSkip} className="text-black/70 hover:text-black text-xs">Passer</Button>
          <Button size="sm" onClick={onNext} className="bg-black text-cyan-400 hover:bg-black/80 text-xs">{step < tooltips.length ? 'Suivant' : 'Terminer'}</Button>
        </div>
        <div className="flex gap-1 justify-center mt-3">
          {tooltips.map((_, idx) => (<div key={idx} className={`w-2 h-2 rounded-full ${idx + 1 <= step ? 'bg-black' : 'bg-black/30'}`} />))}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Badge "OFFERT PAR L'AFFILIATION" - Style Néon Cyberpunk
export const AffiliationBadge = ({ serviceIsFree, deductionPercent, finalCommission }) => {
  if (!serviceIsFree && deductionPercent < 50) return null;
  
  return (
    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="relative inline-flex items-center">
      <div className="absolute inset-0 bg-cyan-400/20 blur-xl rounded-full" />
      <div className={`relative px-4 py-2 rounded-lg border-2 font-mono text-sm font-bold flex items-center gap-2 ${serviceIsFree ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400' : 'border-cyan-400/50 bg-cyan-400/5 text-cyan-300'}`}
        style={{ boxShadow: serviceIsFree ? '0 0 20px rgba(0, 245, 255, 0.4), 0 0 40px rgba(0, 245, 255, 0.2), inset 0 0 20px rgba(0, 245, 255, 0.1)' : '0 0 10px rgba(0, 245, 255, 0.2)', textShadow: serviceIsFree ? '0 0 10px rgba(0, 245, 255, 0.8)' : 'none' }}>
        <div className="absolute top-0 left-0 w-2 h-2 border-t-2 border-l-2 border-cyan-400" />
        <div className="absolute top-0 right-0 w-2 h-2 border-t-2 border-r-2 border-cyan-400" />
        <div className="absolute bottom-0 left-0 w-2 h-2 border-b-2 border-l-2 border-cyan-400" />
        <div className="absolute bottom-0 right-0 w-2 h-2 border-b-2 border-r-2 border-cyan-400" />
        <div className="relative">
          <Gift className="w-5 h-5" />
          {serviceIsFree && <motion.div className="absolute inset-0" animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 2, repeat: Infinity }}><Gift className="w-5 h-5 text-cyan-400" /></motion.div>}
        </div>
        <div className="flex flex-col items-start">
          <span className="text-xs opacity-70">{serviceIsFree ? 'COMMISSION' : `RÉDUCTION ${Math.round(deductionPercent)}%`}</span>
          <span className="tracking-wide">{serviceIsFree ? '0€ - OFFERT' : `${finalCommission}€`}</span>
        </div>
        {serviceIsFree && <div className="ml-2 w-8 h-8 rounded-full border border-cyan-400/50 flex items-center justify-center text-[8px] font-bold opacity-60 rotate-[-15deg]"><span className="text-center leading-none">OFFERT<br/>PAR<br/>AFFIL.</span></div>}
      </div>
      {serviceIsFree && <motion.div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none" initial={{ opacity: 0 }} animate={{ opacity: 1 }}><motion.div className="absolute w-full h-0.5 bg-gradient-to-r from-transparent via-cyan-400 to-transparent" animate={{ y: ['-100%', '200%'] }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }} /></motion.div>}
    </motion.div>
  );
};

// FAQ Accordion Component
export const FAQAccordion = () => {
  const [openItem, setOpenItem] = useState(null);
  const faqs = [
    { q: "Est-ce légal ?", a: "Oui. En signant le Mandat de Succès, vous nous donnez le pouvoir légal (Art. 1984 du Code Civil) d'agir en votre nom." },
    { q: "Combien de temps ?", a: "Notre automate lance la procédure en moins de 24h. Les organismes répondent généralement sous 7-15 jours." },
    { q: "Mes données ?", a: "Vos documents sont chiffrés AES-256. Aucune donnée vendue à des tiers. Suppression automatique à J+30." }
  ];
  
  return (
    <div className="glass-card p-4 border border-gray-800" data-testid="faq-dashboard">
      <h3 className="font-orbitron text-sm text-cyan-400 mb-3 flex items-center gap-2"><HelpCircle className="w-4 h-4" />Aide & Sécurité</h3>
      <div className="space-y-2">
        {faqs.map((faq, idx) => (
          <div key={idx} className="border-b border-gray-800 last:border-0">
            <button onClick={() => setOpenItem(openItem === idx ? null : idx)} className="w-full flex items-center justify-between py-2 text-left text-sm">
              <span className="text-gray-300">{faq.q}</span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${openItem === idx ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {openItem === idx && <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden"><p className="text-gray-500 text-xs pb-2 leading-relaxed">{faq.a}</p></motion.div>}
            </AnimatePresence>
          </div>
        ))}
      </div>
    </div>
  );
};

// V5.21 Progress Stepper Component - "Protocole de Délégation Certifié"
export const ProgressStepper = ({ currentStep, documents, mandats, activeSubscription }) => {
  let actualStep = 1;
  if (documents && documents.length > 0) actualStep = 2;
  if (mandats && mandats.length > 0) actualStep = 3;
  if (activeSubscription?.status === 'processing') actualStep = 4;
  
  const steps = [
    { id: 1, label: 'Protocole de Délégation Certifié', icon: Folder, description: 'Conformité Art. 1984' },
    { id: 2, label: 'Engagement', icon: PenTool, description: 'Mandat de Succès' },
    { id: 3, label: 'Nettoyage', icon: Zap, description: 'Résiliations en cours' },
    { id: 4, label: 'Mission Accomplie', icon: Target, description: 'Économies validées' }
  ];
  
  return (
    <div className="glass-card p-4 md:p-6 border border-gray-800 mb-6 overflow-x-auto">
      {/* V5.21 : Indicateur de conformité */}
      <div className="flex items-center justify-center mb-4 pb-3 border-b border-gray-700">
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-success/10 border border-success/30">
          <Shield className="w-4 h-4 text-success" />
          <span className="text-success text-xs font-mono">Vérification de conformité Art. 1984 active</span>
        </div>
      </div>
      
      <div className="flex items-center justify-between min-w-[500px] md:min-w-0">
        {steps.map((step, idx) => {
          const isCompleted = actualStep > step.id;
          const isActive = actualStep === step.id;
          return (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${isCompleted ? 'bg-success border-success' : isActive ? 'bg-cyan-400/20 border-cyan-400 animate-pulse' : 'bg-gray-800 border-gray-700'}`}>
                  <step.icon className={`w-5 h-5 md:w-6 md:h-6 ${isCompleted ? 'text-black' : isActive ? 'text-cyan-400' : 'text-gray-500'}`} />
                </div>
                <p className={`font-orbitron text-xs md:text-sm mt-2 text-center ${isCompleted ? 'text-success' : isActive ? 'text-cyan-400' : 'text-gray-600'}`}>
                  {idx === 0 ? 'Délégation' : step.label.split(' ')[0]}
                </p>
                <p className="text-gray-500 text-xs hidden md:block">{step.description}</p>
              </div>
              {idx < steps.length - 1 && <div className={`flex-1 h-1 mx-2 md:mx-4 ${isCompleted ? 'bg-success' : 'bg-gray-700'}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Signature Canvas Component
export const SignatureCanvas = ({ onSignatureChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, []);
  
  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };
  
  const startDrawing = (e) => { e.preventDefault(); setIsDrawing(true); const ctx = canvasRef.current.getContext('2d'); const { x, y } = getCoords(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const draw = (e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const { x, y } = getCoords(e); ctx.lineTo(x, y); ctx.stroke(); };
  const stopDrawing = () => { if (isDrawing) { setIsDrawing(false); onSignatureChange(canvasRef.current.toDataURL('image/png')); } };
  const clear = () => { const ctx = canvasRef.current.getContext('2d'); ctx.fillStyle = 'white'; ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height); onSignatureChange(null); };
  
  return (
    <div className="space-y-4">
      <canvas ref={canvasRef} width={400} height={200} className="w-full border-2 border-cyan-400 bg-white cursor-crosshair touch-none"
        onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
        onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} data-testid="signature-canvas" />
      <Button variant="outline" onClick={clear} className="border-gray-600 text-gray-400 w-full md:w-auto" data-testid="clear-signature-btn">Effacer</Button>
    </div>
  );
};

// Mobile Menu Component
export const MobileMenu = ({ activeTab, setActiveTab, user, onLogout, isOpen, setIsOpen }) => {
  const menuItems = [
    { id: 'overview', label: 'Aperçu', icon: Home },
    { id: 'payment', label: 'Paiement', icon: CreditCard },
    { id: 'mandat', label: 'Mandat', icon: PenTool },
    // 🔴 P0 FIX : Suppression de l'onglet Documents du menu mobile
    { id: 'audit', label: 'Audit IA', icon: Scan },
    { id: 'proofs', label: 'Mes Preuves', icon: Shield },
    { id: 'referral', label: 'Parrainage', icon: Gift },
  ];
  
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 z-40 md:hidden" onClick={() => setIsOpen(false)} />
          <motion.div initial={{ x: '-100%' }} animate={{ x: 0 }} exit={{ x: '-100%' }} transition={{ type: 'tween' }} className="fixed left-0 top-0 bottom-0 w-72 bg-void border-r border-gray-800 z-50 md:hidden overflow-y-auto">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <Link to="/" className="font-orbitron text-xl font-bold text-cyan-400">L'EFFACEUR</Link>
              <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 bg-cyan-400/5 border-b border-gray-800">
              <p className="text-cyan-400 font-orbitron font-bold truncate">{user?.name || user?.email?.split('@')[0]}</p>
              <p className="text-gray-500 text-xs font-mono truncate">{user?.email}</p>
            </div>
            <nav className="p-2">
              {menuItems.map((item) => (
                <button key={item.id} onClick={() => { setActiveTab(item.id); setIsOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-4 mb-1 transition-all ${activeTab === item.id ? 'bg-cyan-400/10 border-l-4 border-cyan-400 text-cyan-400' : 'text-gray-400 hover:bg-gray-800'}`} data-testid={`mobile-nav-${item.id}`}>
                  <item.icon className="w-6 h-6" /><span className="font-medium">{item.label}</span>
                </button>
              ))}
            </nav>
            <div className="p-4 border-t border-gray-800 mt-4 space-y-2">
              <Link to="/" className="w-full flex items-center gap-3 px-4 py-3 text-gray-400 hover:bg-gray-800 rounded"><Home className="w-5 h-5" />Retour à l'accueil</Link>
              <Button variant="ghost" onClick={onLogout} className="w-full justify-start text-red-400 hover:bg-red-400/10 py-4"><LogOut className="w-5 h-5 mr-3" />Déconnexion</Button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Sidebar - Desktop
export const Sidebar = ({ activeTab, setActiveTab, user, onLogout, activeSubscription, pushNotifications }) => {
  const packType = activeSubscription?.pack_type;
  const isDemenagement = packType === 'mission_demenagement' || packType === 'nouveau_depart';
  const isHeritage = packType === 'mission_heritage' || packType === 'serenite_succession';
  
  const menuItems = [
    { id: 'overview', label: 'Aperçu', icon: Home },
    { id: 'payment', label: 'Paiement', icon: CreditCard, highlight: !activeSubscription },
    { id: 'mission-form', label: isDemenagement ? 'Mission Déménagement' : isHeritage ? 'Mission Succession' : 'Ma Mission', icon: isDemenagement ? Home : isHeritage ? FileText : Package, highlight: activeSubscription && !activeSubscription?.mission_details_completed, hidden: !activeSubscription },
    { id: 'mandat', label: 'Mandat', icon: PenTool, locked: !activeSubscription?.can_sign_mandat },
    // 🔴 P0 FIX : Suppression de l'onglet Documents - Intégré dans chaque mission
    { id: 'audit', label: 'Audit IA', icon: Scan, highlight: activeSubscription?.pack_type === 'audit_pouvoir_achat' || activeSubscription?.pack_type === 'mission_audit' },
    { id: 'proofs', label: 'Mes Preuves', icon: Shield },
    { id: 'referral', label: 'Parrainage', icon: Gift, special: true },
    { id: 'assistant', label: 'Assistant du Bug', icon: Bot, aiPowered: true },
    { id: 'data-security', label: 'Données & Sécurité', icon: Lock },
  ].filter(item => !item.hidden);
  
  return (
    <aside className="hidden md:flex w-64 bg-accent/50 border-r border-gray-800 flex-col" data-testid="sidebar">
      <div className="p-6 border-b border-gray-800">
        <Link to="/" className="block hover:opacity-80 transition-opacity">
          <h1 className="font-orbitron text-2xl font-bold text-cyan-400 text-center" style={{ textShadow: '0 0 20px rgba(0, 245, 255, 0.5)' }}>L'EFFACEUR</h1>
          <p className="text-gray-500 text-xs text-center font-mono mt-1">// DASHBOARD</p>
        </Link>
      </div>
      <nav className="flex-1 p-4">
        <Link to="/" className="w-full flex items-center gap-3 px-4 py-3 mb-2 text-gray-400 hover:bg-gray-800 transition-all font-mono text-sm border border-gray-700/50 hover:border-cyan-400/50 mb-4"><Home className="w-5 h-5" /><span>Retour à l'accueil</span></Link>
        {menuItems.map((item) => (
          <button key={item.id} onClick={() => setActiveTab(item.id)} disabled={item.locked}
            className={`w-full flex items-center gap-3 px-4 py-3 mb-2 transition-all font-mono text-sm ${activeTab === item.id ? 'bg-cyan-400/10 border-l-2 border-cyan-400 text-cyan-400' : item.locked ? 'text-gray-600 cursor-not-allowed' : item.special ? 'text-magenta-400 hover:bg-magenta-400/10 border border-magenta-400/30' : item.aiPowered ? 'text-success hover:bg-success/10 border border-success/30' : item.highlight ? 'text-success hover:bg-success/10' : 'text-gray-400 hover:bg-gray-800'}`} data-testid={`nav-${item.id}`}>
            <item.icon className="w-5 h-5" /><span>{item.label}</span>
            {item.locked && <Lock className="w-4 h-4 ml-auto" />}
            {item.special && <Sparkles className="w-4 h-4 ml-auto animate-pulse" />}
            {item.aiPowered && <Sparkles className="w-4 h-4 ml-auto text-success animate-pulse" />}
          </button>
        ))}
      </nav>
      <div className="px-4 pb-4"><FAQAccordion /></div>
      <div className="p-4 border-t border-gray-800">
        {/* Section Alertes mise en avant */}
        {pushNotifications?.isSupported && (
          <div className={`mb-4 p-3 rounded-lg transition-all ${pushNotifications.isSubscribed ? 'bg-success/10 border border-success/30' : 'bg-yellow-400/10 border border-yellow-400/30 animate-pulse'}`}>
            <div className="flex items-center gap-2 mb-2">
              <Bell className={`w-4 h-4 ${pushNotifications.isSubscribed ? 'text-success' : 'text-yellow-400'}`} />
              <span className={`font-mono text-xs font-bold ${pushNotifications.isSubscribed ? 'text-success' : 'text-yellow-400'}`}>
                {pushNotifications.isSubscribed ? 'Alertes activées' : 'Alertes recommandées'}
              </span>
            </div>
            {!pushNotifications.isSubscribed && (
              <p className="text-gray-400 text-xs mb-2">Recevez les mises à jour de vos résiliations en temps réel</p>
            )}
            <button 
              onClick={pushNotifications.toggle} 
              disabled={pushNotifications.isLoading}
              className={`w-full flex items-center justify-center gap-2 py-2 transition-all font-mono text-xs rounded ${
                pushNotifications.isSubscribed 
                  ? 'bg-success/20 text-success hover:bg-success/30' 
                  : 'bg-yellow-400 text-black hover:bg-yellow-300 font-bold'
              }`} 
              data-testid="alerts-toggle"
            >
              <Bell className="w-3 h-3" />
              {pushNotifications.isLoading ? 'Chargement...' : pushNotifications.isSubscribed ? 'Désactiver' : 'Activer maintenant'}
            </button>
          </div>
        )}
        <Link to="/profile" className="flex items-center gap-3 mb-4 p-3 bg-gradient-to-r from-cyan-500/20 to-magenta-500/20 border border-cyan-400/50 hover:border-cyan-400 hover:from-cyan-500/30 hover:to-magenta-500/30 transition-all group" data-testid="profile-link">
          <div className="w-10 h-10 bg-cyan-400/30 group-hover:bg-cyan-400/40 flex items-center justify-center transition-colors"><User className="w-5 h-5 text-cyan-400" /></div>
          <div className="flex-1 min-w-0"><p className="text-cyan-400 text-sm font-bold font-orbitron">MON COMPTE</p><p className="text-gray-400 text-xs truncate">{user?.email}</p></div>
          <ChevronRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
        </Link>
        <Button variant="ghost" onClick={onLogout} className="w-full justify-start text-red-400 hover:bg-red-400/10" data-testid="logout-btn"><LogOut className="w-4 h-4 mr-2" />Déconnexion</Button>
      </div>
    </aside>
  );
};
